import React, { useMemo, useEffect } from 'react';
import { useSize } from 'ahooks';
import { v4 as uuidv4 } from 'uuid';
import TableActions from './TableActions';
import { useTableActions } from './useTableActions';
import { useAgentStoreContext } from '../../hooks/useLocalState';
import { useSessionId } from '../../hooks/useAgentModalSata';
import './tableComponents.scss';
import { useCaseInsensitiveParams, useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { useGlobalReportManager } from '../../../components/ReportViewer/useGlobalReportManager';

interface ITableWrapperProps {
  node?: any;
  children?: React.ReactNode;
  from?: string;
  messageId: string;
  answer?: any[];
  [key: string]: any;
}

const TableWrapper: React.FC<ITableWrapperProps> = ({ node, children, from, messageId, answer, ...props }) => {
  // 为每个表格生成唯一的 UUID，用于精确定位
  const tableId = useMemo(() => uuidv4(), []);
  const { getItem } = useAgentStoreContext();
  const { sessionId: routeSessionId, originalSessionId } = useSessionId();
  const { snapshotId, poolId, msgid } = useCaseInsensitiveParams(useSearchParamsAppoint(), ["snapshotId", 'poolId', 'msgid']);
  const streamingState = getItem(`__dba_chat_history_streaming_state_${routeSessionId}__`);
  // 统一使用全局报告管理器管理当前会话的 dsReport 状态，避免依赖旧的局部缓存 key
  const { activeReport } = useGlobalReportManager({ sessionId: routeSessionId });
  const dsReport = activeReport || { show: false };
  const isComplete = (['complete', 'rejected'].includes(streamingState?.current as string) || !streamingState?.current);
  const answerContainer = dsReport?.show ? '.dba-chat-ds-report-page-right-markdown-box' : '.dba-answer';
  // 获取表格的宽度 start,
  // 解决的动机是些页面有拖拽功能，此时的宽度是会变化的，但是底部的操作栏需要固定在页面，不可以被遮盖
  // TODO: css暂时搞不定... 
  // 根据 from 的值动态选择容器元素
  const boxSize = useSize(() => {
    let boxSelector = '.dba-switch-agent-accordion-content-items'; // 默认值
    if (from === 'agent') {
      boxSelector = '.dba-chat-content';
    } 
    if (!originalSessionId) {
      boxSelector = '.dba-chat-scroll';
    }
    if (from === 'think') {
      boxSelector = '.dba-switch-agent-accordion-content-items';
    } 
    if (dsReport?.show || snapshotId || poolId || msgid) {
      boxSelector = '.dba-chat-ds-report-page-right-markdown-box';
      // 如果目标元素不存在，使用 .dba-chat-content 作为备选
      const element = document.querySelector(boxSelector);
      if (!element) {
        boxSelector = '.dba-chat-content';
      }
    }
    return document.querySelector(boxSelector);
  }) || { width: 1000 };

  // 获取这个下面的thead
  const currentTable = useSize(() => {
    // 获取表格元素
    const table = document.querySelector(`table[data-table-id="${tableId}"]`);
    if (!table) return null;
    // 从表格中获取 thead
    const thead = table.querySelector('thead');
    return thead;
  });
  const isMaxWidth = (currentTable?.width || 0) >= (boxSize?.width || 0);
  // 获取表格的宽度 end

  // 判断单元格文字长度的函数
  const processCellText = (cell: Element) => {
    // 避免重复处理已标记的单元格
    if (cell.hasAttribute('data-text-processed')) return;
    
    const textContent = cell.textContent?.trim() || '';
    // 统计中文字符数量（包括中文标点符号）
    const chineseCharCount = (textContent.match(/[\u4e00-\u9fa5\u3000-\u303f\uff00-\uffef]/g) || []).length;
    // 判断中文字符数量是否 <= 8
    if (chineseCharCount <= 8) {
      cell.setAttribute('data-short-text', 'true');
    } else {
      cell.removeAttribute('data-short-text');
    }
    // 标记为已处理，避免重复判断
    cell.setAttribute('data-text-processed', 'true');
  };

  // 动态判断单元格文字长度，支持流式追加的表格
  useEffect(() => {
    const table = document.querySelector(`table[data-table-id="${tableId}"]`);
    if (!table) return;

    // 初始化：处理已存在的表头单元格（只处理 th）
    const initialCells = table.querySelectorAll('th');
    initialCells.forEach(processCellText);

    // 使用 MutationObserver 监听新增的表头单元格
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // 处理新增的行
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // 如果新增的是 tr，处理其中的 th（只处理表头）
            if (element.tagName === 'TR') {
              const cells = element.querySelectorAll('th');
              cells.forEach(processCellText);
            }
            // 如果新增的直接是 th
            if (element.tagName === 'TH') {
              processCellText(element);
            }
            // 处理嵌套情况（如 thead 中新增 tr）
            const nestedCells = element.querySelectorAll('th');
            nestedCells.forEach(processCellText);
          }
        });
      });
    });

    // 监听表格的子节点变化
    observer.observe(table, {
      childList: true,
      subtree: true,
    });

    // 清理函数
    return () => {
      observer.disconnect();
    };
  }, [tableId]);
  // 使用 Hook 获取默认的表格操作配置
  const tableActions = useTableActions({
    node,
    tableId,
    answerContainer,
    messageId,
    answer: answer || []
  });

  return (
    <div className="dba-table-wrapper"
      key={tableId}
      style={{
        width: isMaxWidth ? '100%' : 'auto',
      }}
    >
      <table {...props} data-table-id={tableId}>{children}</table>
      {isComplete && (
        <TableActions
          actions={tableActions}
        />
      )}
    </div>
  );
};

export default React.memo(TableWrapper);
